import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Shield,
  Save,
  ChevronDown,
  ChevronRight,
  Search,
  Check,
  X,
  UserPlus,
  ExternalLink,
  Link as LinkIcon,
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserPermissions() {
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [schema, setSchema] = useState({ categories: {}, all_keys: [] });
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingUser, setLinkingUser] = useState(null);
  const [personnelSearch, setPersonnelSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, levelsRes, schemaRes, personnelRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/permissions/levels`),
        axios.get(`${API}/permissions/schema`),
        axios.get(`${API}/personnel`),
      ]);
      setUsers(usersRes.data);
      setLevels(levelsRes.data);
      setSchema(schemaRes.data);
      setPersonnel(personnelRes.data);
      
      // Expand all categories by default
      const expanded = {};
      Object.keys(schemaRes.data.categories).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);
    try {
      const response = await axios.get(`${API}/users/${user.id}/permissions`);
      setUserPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      toast.error('Failed to load user permissions');
    }
  };

  const handleLinkPersonnel = async (userId, personnelId) => {
    try {
      await axios.post(`${API}/users/${userId}/link-personnel`, {
        personnel_id: personnelId,
      });
      toast.success('User linked to personnel profile');
      setShowLinkModal(false);
      setLinkingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link personnel');
    }
  };

  const handleCreatePersonnel = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/link-personnel`, {
        create_new: true,
      });
      toast.success('Personnel profile created');
      setShowLinkModal(false);
      setLinkingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create personnel');
    }
  };

  const getPersonnelForUser = (user) => {
    if (!user.personnel_id) return null;
    return personnel.find(p => p.id === user.personnel_id);
  };

  const getUnlinkedPersonnel = () => {
    const linkedIds = users.map(u => u.personnel_id).filter(Boolean);
    return personnel.filter(p => !linkedIds.includes(p.id) && !p.user_id);
  };

  const handleSavePermissions = async () => {
    if (!editingUser || !userPermissions) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}/permissions`, {
        level_id: userPermissions.use_custom ? null : userPermissions.level_id,
        use_custom: userPermissions.use_custom,
        custom_permissions: userPermissions.custom_permissions || {},
      });
      toast.success('Permissions updated');
      setEditingUser(null);
      setUserPermissions(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleLevelChange = (levelId) => {
    if (levelId === 'custom') {
      // Switch to custom permissions
      const currentPerms = userPermissions.use_custom 
        ? userPermissions.custom_permissions 
        : userPermissions.permissions;
      setUserPermissions({
        ...userPermissions,
        use_custom: true,
        level_id: null,
        custom_permissions: { ...currentPerms },
      });
    } else {
      // Switch to a level
      const level = levels.find(l => l.id === levelId);
      setUserPermissions({
        ...userPermissions,
        use_custom: false,
        level_id: levelId,
        level_name: level?.name,
        permissions: level?.permissions || {},
      });
    }
  };

  const togglePermission = (permKey) => {
    if (!userPermissions.use_custom) return;
    
    setUserPermissions({
      ...userPermissions,
      custom_permissions: {
        ...userPermissions.custom_permissions,
        [permKey]: !userPermissions.custom_permissions[permKey],
      },
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query)
    );
  });

  const getEffectivePermissions = () => {
    if (!userPermissions) return {};
    if (userPermissions.use_custom) {
      return userPermissions.custom_permissions || {};
    }
    return userPermissions.permissions || {};
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">User Permissions</h1>
        <p className="text-muted-foreground">
          Assign permission levels or custom permissions to users
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User List */}
        <Card className="p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredUsers.map((user) => {
              const linkedPersonnel = getPersonnelForUser(user);
              return (
                <div
                  key={user.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    editingUser?.id === user.id
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-muted/50'
                  }`}
                >
                  <button
                    onClick={() => handleEditUser(user)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                        user.role === 'admin' ? 'bg-amber-500' : 'bg-primary'
                      }`}>
                        {(user.first_name?.[0] || user.email?.[0] || 'U').toUpperCase()}
                      </div>
                      <div className="text-left">
                        <div className="font-medium">
                          {user.first_name && user.last_name 
                            ? `${user.last_name}, ${user.first_name}`
                            : user.email}
                        </div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.role === 'admin' ? (
                        <Badge className="bg-amber-100 text-amber-700">Admin</Badge>
                      ) : (
                        <Badge variant="secondary">
                          {user.permission_level_name || 'No Level'}
                        </Badge>
                      )}
                    </div>
                  </button>
                  
                  {/* Personnel Profile Link */}
                  <div className="mt-2 pt-2 border-t flex items-center justify-between">
                    {linkedPersonnel ? (
                      <Link 
                        to={`/personnel/${linkedPersonnel.id}`}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <ExternalLink size={12} />
                        View Profile ({linkedPersonnel.first_name} {linkedPersonnel.last_name})
                      </Link>
                    ) : (
                      <span className="text-sm text-muted-foreground">No personnel profile</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLinkingUser(user);
                        setShowLinkModal(true);
                      }}
                      className="text-xs gap-1"
                    >
                      <LinkIcon size={12} />
                      {linkedPersonnel ? 'Change' : 'Link Profile'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Permission Editor */}
        <Card className="p-6">
          {editingUser ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">
                    {editingUser.first_name && editingUser.last_name
                      ? `${editingUser.first_name} ${editingUser.last_name}`
                      : editingUser.email}
                  </h2>
                  <p className="text-sm text-muted-foreground">{editingUser.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setEditingUser(null); setUserPermissions(null); }}>
                  <X size={16} />
                </Button>
              </div>

              {editingUser.role === 'admin' ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Shield size={20} />
                    <span className="font-medium">Administrator</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">
                    Admin users have full access to all features. Their permissions cannot be modified.
                  </p>
                </div>
              ) : userPermissions ? (
                <>
                  {/* Level Selection */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Permission Level</label>
                    <div className="space-y-2">
                      {levels.filter(l => !l.is_admin).map((level) => (
                        <label
                          key={level.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                            !userPermissions.use_custom && userPermissions.level_id === level.id
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:bg-muted/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="permission_level"
                              checked={!userPermissions.use_custom && userPermissions.level_id === level.id}
                              onChange={() => handleLevelChange(level.id)}
                              className="h-4 w-4"
                            />
                            <div>
                              <div className="font-medium">{level.name}</div>
                              {level.description && (
                                <div className="text-xs text-muted-foreground">{level.description}</div>
                              )}
                            </div>
                          </div>
                          {level.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </label>
                      ))}
                      
                      {/* Custom Permissions Option */}
                      <label
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          userPermissions.use_custom
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:bg-muted/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="permission_level"
                          checked={userPermissions.use_custom}
                          onChange={() => handleLevelChange('custom')}
                          className="h-4 w-4"
                        />
                        <div>
                          <div className="font-medium">Custom Permissions</div>
                          <div className="text-xs text-muted-foreground">
                            Select individual permissions below
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Custom Permissions */}
                  {userPermissions.use_custom && (
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-medium mb-3">Custom Permissions</h3>
                      <div className="space-y-3 max-h-[350px] overflow-y-auto">
                        {Object.entries(schema.categories).map(([catKey, category]) => {
                          const isExpanded = expandedCategories[catKey];
                          const categoryPerms = Object.keys(category.permissions);
                          const perms = getEffectivePermissions();
                          const selectedCount = categoryPerms.filter(k => perms[k]).length;
                          
                          return (
                            <div key={catKey} className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => toggleCategory(catKey)}
                                className="w-full flex items-center justify-between p-2 bg-muted/30 hover:bg-muted/50 transition-colors text-sm"
                              >
                                <div className="flex items-center gap-2">
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <span className="font-medium">{category.label}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {selectedCount}/{categoryPerms.length}
                                  </Badge>
                                </div>
                              </button>
                              
                              {isExpanded && (
                                <div className="p-2 space-y-1">
                                  {Object.entries(category.permissions).map(([permKey, permLabel]) => (
                                    <label
                                      key={permKey}
                                      className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-sm"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={perms[permKey] || false}
                                        onChange={() => togglePermission(permKey)}
                                        className="h-3.5 w-3.5 rounded"
                                      />
                                      <span>{permLabel}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Permission Preview (for level selection) */}
                  {!userPermissions.use_custom && userPermissions.level_id && (
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-medium mb-3">Level Permissions Preview</h3>
                      <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                        {Object.entries(schema.categories).flatMap(([catKey, category]) =>
                          Object.entries(category.permissions)
                            .filter(([permKey]) => userPermissions.permissions[permKey])
                            .map(([permKey, permLabel]) => (
                              <div key={permKey} className="flex items-center gap-2 text-sm text-green-700">
                                <Check size={14} />
                                <span>{permLabel}</span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSavePermissions} disabled={saving} className="gap-2">
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Permissions'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Users className="text-muted-foreground mb-4" size={48} />
              <h3 className="text-lg font-medium mb-2">Select a User</h3>
              <p className="text-muted-foreground">
                Choose a user from the list to view and edit their permissions
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Link Personnel Modal */}
      {showLinkModal && linkingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Link Personnel Profile</h2>
                <p className="text-sm text-muted-foreground">
                  For user: {linkingUser.email}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowLinkModal(false); setLinkingUser(null); }}>
                <X size={16} />
              </Button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {/* Create New Option */}
              <div className="mb-4 p-4 border-2 border-dashed rounded-lg">
                <h3 className="font-medium mb-2">Create New Personnel Profile</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Create a new personnel record for this user with their basic info
                </p>
                <Button onClick={() => handleCreatePersonnel(linkingUser.id)} className="gap-2">
                  <UserPlus size={16} />
                  Create Personnel Profile
                </Button>
              </div>

              {/* Link to Existing */}
              <div>
                <h3 className="font-medium mb-2">Or Link to Existing Personnel</h3>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                  <Input
                    placeholder="Search personnel..."
                    value={personnelSearch}
                    onChange={(e) => setPersonnelSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {getUnlinkedPersonnel()
                    .filter(p => {
                      const query = personnelSearch.toLowerCase();
                      return (
                        p.first_name?.toLowerCase().includes(query) ||
                        p.last_name?.toLowerCase().includes(query) ||
                        p.email?.toLowerCase().includes(query)
                      );
                    })
                    .map(p => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30"
                      >
                        <div>
                          <div className="font-medium">
                            {p.last_name}, {p.first_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.email || 'No email'} • {p.personnel_type} • {p.division}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLinkPersonnel(linkingUser.id, p.id)}
                          className="gap-1"
                        >
                          <LinkIcon size={14} />
                          Link
                        </Button>
                      </div>
                    ))}
                  {getUnlinkedPersonnel().length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No unlinked personnel records found
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t">
              <Button variant="outline" onClick={() => { setShowLinkModal(false); setLinkingUser(null); }} className="w-full">
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
